/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { t } from '@sportninja/common/i18n'
import colors from '@sportninja/common/constants/appColors'
import Icon from 'src/components/Icon'

const containerStyle = css`
  width: 100%;
  height: 100%;
  background-color: ${colors.NEUTRAL_900};
  display: flex;
  justify-content: center;
  align-items: center;
`

const contentStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 24px;
  text-align: center;
`

const titleStyle = css`
  color: ${colors.NEUTRAL_0};
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
`

const subtitleStyle = css`
  color: ${colors.NEUTRAL_100};
  font-size: 14px;
  margin-bottom: 24px;
`

const primaryButtonStyle = css`
  display: flex;
  align-items: center;
  background-color: ${colors.PRIMARY};
  padding: 12px 20px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  margin-bottom: 12px;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${colors.PRIMARY_400};
  }
`

const buttonTextStyle = css`
  color: ${colors.NEUTRAL_0};
  font-size: 14px;
  font-weight: 600;
  margin-left: 8px;
`

const hintStyle = css`
  color: ${colors.NEUTRAL_200};
  font-size: 12px;
  margin-top: 8px;
`

const iconStyle = css`
  margin-bottom: 16px;
`

/**
 * Overlay component for broadcast states (scheduled, ended, error)
 * Maintains same dimensions as video player to prevent layout shift
 */
const BroadcastStateOverlay = ({
  state,
  onRetry,
  onWatchReplay,
  hasReplayUrl,
  error,
}) => {
  const renderScheduledState = () => (
    <div css={contentStyle}>
      <h3 css={titleStyle}>{t('GameScreen:streamStartingSoon')}</h3>
      <p css={subtitleStyle}>{t('GameScreen:streamStartingSoonSubtitle')}</p>
    </div>
  )

  const renderEndedState = () => (
    <div css={contentStyle}>
      <div css={iconStyle}>
        <Icon name='video' fontSize={48} color={colors.NEUTRAL_100} />
      </div>
      <h3 css={titleStyle}>{t('GameScreen:streamEnded')}</h3>
      <p css={subtitleStyle}>{t('GameScreen:streamEndedSubtitle')}</p>
      {hasReplayUrl && onWatchReplay && (
        <button
          css={[primaryButtonStyle, { display: 'none' }]}
          onClick={onWatchReplay}
        >
          <Icon name='play' fontSize={20} color={colors.NEUTRAL_0} />
          <span css={buttonTextStyle}>{t('GameScreen:watchReplay')}</span>
        </button>
      )}
    </div>
  )

  const renderErrorState = () => (
    <div css={contentStyle}>
      <div css={iconStyle}>
        <Icon
          name='exclamation-circle'
          fontSize={48}
          color={colors.ERROR_300}
        />
      </div>
      <h3 css={titleStyle}>{t('GameScreen:streamError')}</h3>
      <p css={subtitleStyle}>{error || t('GameScreen:streamErrorSubtitle')}</p>
      {onRetry && (
        <button css={primaryButtonStyle} onClick={onRetry}>
          <Icon name='sync' fontSize={20} color={colors.NEUTRAL_0} />
          <span css={buttonTextStyle}>{t('common:retry')}</span>
        </button>
      )}
      <span css={hintStyle}>{t('GameScreen:checkConnection')}</span>
    </div>
  )

  const renderContent = () => {
    switch (state) {
      case 'scheduled':
        return renderScheduledState()
      case 'ended':
        return renderEndedState()
      case 'error':
        return renderErrorState()
      default:
        return null
    }
  }

  if (state === 'idle' || state === 'live') {
    return null
  }

  return <div css={containerStyle}>{renderContent()}</div>
}

export default BroadcastStateOverlay
